import CloseIcon from "@mui/icons-material/Close";
import { Box, DialogActions, Fade, Slide, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React, { forwardRef } from "react";
import { alignItemsCenter, justifyContentCenter } from "../../../../styles";
import Icon from "../../Icons";
import Text from "../../Typography/Text";
import { CustomModalProps, TransitionProps } from "./index.types";

const TransitionSlide = forwardRef<HTMLDivElement, TransitionProps>((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TransitionFade = forwardRef<HTMLDivElement, TransitionProps>((props, ref) => {
  return <Fade ref={ref} {...props} />;
});

const CustomModal: React.FC<CustomModalProps> = ({
  titleIcon = null,
  actions = null,
  fade = true,
  noHeader = false,
  children = null,
  title = "",
  open = false,
  setOpen = () => {},
  width = "auto",
  maxWidth = "md",
  fullWidth = false,
  className = "",
  showCloseIcon = true,
  "aria-labelledby": ariaLabelledBy = "scroll-dialog-title",
  "aria-describedby": ariaDescribedBy = "scroll-dialog-description",
  sxDialog = {},
  sxDialogContent = {},
  sxTitleProps = {},
}) => {
  const padding = "24px";
  const theme = useTheme();

  const handleClose = (): void => {
    setOpen && setOpen(false);
  };

  const titleStyling = {
    color: "black",
    marginTop: "0px",
    marginBottom: "0px",
    letterSpacing: "1px",
    display: "flex",
    ...justifyContentCenter,
    ...alignItemsCenter,
  };

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={open}
      slots={{
        transition: fade ? TransitionFade : TransitionSlide,
      }}
      onClose={handleClose}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      sx={{ ...sxDialog, width: width }}
      data-testid={"modal-testid"}>
      {noHeader ? null : (
        <DialogTitle
          id="scroll-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <Text variant="bodyS" {...titleStyling}>
            <Text variant="h2" data-testid={"modal-title"} sx={{ fontWeight: "700", ...sxTitleProps }}>
              {title}{" "}
            </Text>
            <span style={{ marginLeft: "4px" }}>{titleIcon && titleIcon}</span>
          </Text>
          {showCloseIcon && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ cursor: "pointer" }}>
              <Icon
                icon={<CloseIcon sx={{ color: theme?.palette?.common?.black }} data-testid={"close-custom-modal-icon"} />}
                color={theme.palette.common.white}
              />
            </Box>
          )}
        </DialogTitle>
      )}
      {/* TODO - the padding of the primary content and the 'actions' areas should be different as per the deesigns */}
      {/* Most often, 24 px bottom and side padding on the actions item. With 32 top and 40 side padding on the content */}
      <DialogContent
        className={className}
        sx={{
          minWidth: 500,
          "&.MuiDialogContent-root": {
            paddingTop: "24px",
          },
          padding,
          ...sxDialogContent,
        }}>
        <Box data-testid={"modal-content"}>{children}</Box>
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "#D9D9D9",
            padding: "10px 0px",
            display: "block",
          }}>
          {actions.map((action, index) => (
            <Box
              key={index}
              sx={{
                padding: "10px 20px",
              }}>
              {action}
            </Box>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CustomModal;
